import {
  type CollectionReference,
  type DocumentData,
  type Firestore,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  type WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import { type ObserveOps, createObserveOps } from './observe-ops.js';

/**
 * ARCHIVER, DESARCHIVER, SUPPRIMER : la semantique des trois etats, ecrite UNE
 * fois pour les 55 familles.
 *
 * Avant ce module, la doctrine disait depuis le premier jour que supprimer
 * c'est archiver en douceur, `archivedAt` existait sur les 57 familles, les
 * Security Rules l'exigeaient et les listes le filtraient cote serveur, mais
 * AUCUNE methode du SDK ne permettait de l'ecrire : le champ etait pose a la
 * creation et plus jamais touche. Et il n'existait rien du tout pour supprimer,
 * les 55 blocs de rules portant `allow delete: if false`. Chaque app se serait
 * donc debrouillee seule, c'est-a-dire differemment, c'est-a-dire faux quelque
 * part.
 *
 * LES TROIS ETATS, et c'est tout le contrat :
 *
 *   Actif    : archivedAt = null,  deletedAt = null   -> toutes les listes
 *   Archive  : archivedAt = date,  deletedAt = null   -> le mode Archives seul
 *   Supprime : archivedAt = date,  deletedAt = date   -> NULLE PART cote client
 *
 * POURQUOI SUPPRIMER POSE LES DEUX CHAMPS, et pas seulement `deletedAt` : le
 * filtre serveur `where('archivedAt', '==', null)` existe deja dans `runList`
 * (list-query.ts) et les index composites deja declares le portent deja. Un
 * document supprime etant aussi archive, il sort de toutes les listes SANS
 * qu'on modifie une seule requete ni un seul index. C'est ce qui rend la
 * suppression possible sans toucher au socle de lecture.
 *
 * POUR L'UTILISATEUR, SUPPRIME VEUT DIRE SUPPRIME. La fiche quitte les listes ET
 * les archives, et rien dans l'interface ne la recupere : ne construisez ni
 * ecran, ni bouton, ni mention de restauration. La ligne survit en base pour que
 * le support Taki puisse rattraper une betise client ; c'est un garde-fou
 * d'exploitation, pas une corbeille. Rien ne la purge encore : la purge exige
 * des Cloud Functions et vit dans son propre chantier
 * (`purge-corbeille-30-jours`).
 *
 * LE PIEGE A NE PAS RATER, celui qui coute des donnees : `unarchive` REECRIT
 * `archivedAt: null`, il ne fait JAMAIS `deleteField()`. Deux raisons, et la
 * deuxieme est silencieuse. Les Security Rules interdisent de retirer la cle
 * (helper `keepsArchivedAt()`, `firebase/firestore.rules`, dont le commentaire
 * porte le raisonnement complet : allez le relire avant de toucher a ce
 * module). Et surtout : un filtre `== null` ne rattrape JAMAIS un champ ABSENT,
 * donc un document prive de sa cle disparaitrait de TOUTES les listes et de TOUS
 * les compteurs de l'org, sans une seule erreur nulle part. Ce n'est pas un
 * desarchivage, c'est une suppression deguisee, et elle ne se voit qu'une fois
 * les donnees perdues. Meme raison mot pour mot pour `deletedAt`.
 *
 * DEUXIEME PIEGE, celui qui coute une lecture : `remove` doit connaitre l'etat
 * du document AVANT d'ecrire. `serverTimestamp()` est une sentinelle resolue par
 * le serveur, elle ne se conditionne pas apres coup et ne se compare pas a
 * l'existant : impossible d'ecrire "pose `archivedAt` seulement s'il etait nul"
 * en une seule ecriture. D'ou une lecture prealable, assumee. Le change est bon :
 * une lecture de plus contre la date d'archivage REELLE d'un document archive il
 * y a six mois, qui serait sinon ecrasee par l'heure de sa suppression : c'est
 * exactement l'information que le support cherchera. Une suppression est un
 * geste rare et unitaire, jamais une boucle.
 *
 * APRES UNE SUPPRESSION, PLUS AUCUNE ECRITURE CLIENT. Les rules refusent toute
 * mise a jour d'un document dont `deletedAt` est deja pose (la condition porte
 * sur `resource.data`, le document AVANT ecriture : elle laisse donc passer la
 * suppression elle-meme, et bloque tout ce qui vient apres). Un `archive`, un
 * `unarchive` ou un `update` sur un document supprime echoue en
 * `permission-denied`, cote serveur : c'est voulu, ce n'est pas un bug a
 * contourner.
 *
 * NOTE SUR `archive` : il REECRIT `archivedAt` meme si le document etait deja
 * archive, donc il ecrase la date precedente. C'est assume et c'est la
 * difference volontaire avec `remove` : archiver est un geste explicite de
 * l'utilisateur sur cette fiche, la date la plus recente est la bonne, alors que
 * `remove` n'a jamais recu l'ordre d'archiver quoi que ce soit.
 *
 * POURQUOI UNE FABRIQUE, et pas trois fonctions libres appelees par chaque
 * repository : le cablage doit tenir en DEUX lignes par famille, sinon 55
 * copies de trois methodes divergeront exactement comme les listes ont diverge
 * avant list-query.ts. Un objet construit une fois a cote de `col`, puis etale
 * dans l'objet rendu (`...archiveOps`), ne laisse aucune place a une variante
 * locale : il n'y a plus de corps de methode a ecrire, donc plus rien a ecrire
 * differemment.
 *
 * @param db - l'instance Firestore, pour ecrire par une ref BRUTE (sans
 *   converter) : une ref convertie exigerait le document complet a chaque
 *   ecriture partielle, exactement comme dans `update()` de chaque repository.
 * @param col - la collection AVEC son converter, pour RELIRE le document ecrit
 *   et le rendre valide par son schema Zod.
 * @param collectionName - le nom de la collection, pour la ref brute et pour des
 *   messages d'erreur qui nomment la famille.
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`). Les Security Rules exigent
 *   `updatedBy == request.auth.uid` a chaque ecriture.
 */

/**
 * CE QU'UNE FAMILLE AJOUTE A LA SUPPRESSION, et rien d'autre : des ecritures
 * dans le MEME LOT que la suppression douce. C'est le cas d'un plan recurrent,
 * qui se retire de la liste `plansIds` de son modele (recurrences.ts) : les
 * Security Rules exigent que les deux ecritures arrivent ensemble, l'une sans
 * l'autre est refusee. La semantique des trois etats ne change pas.
 */
export interface ArchiveHooks {
  /**
   * Ajoute ses ecritures au lot de la suppression. `avant` est le document tel
   * qu'il a ete lu juste avant (brut, sans converter) : pas de seconde lecture.
   */
  avantSuppression?: (id: string, batch: WriteBatch, avant: DocumentData) => Promise<void>;
}

/**
 * Les trois verbes, identiques sur les 55 familles, et la LECTURE EN DIRECT
 * d'une fiche (`observe`, observe-ops.ts).
 *
 * POURQUOI `observe` VOYAGE ICI, et pas dans chaque repository : cette fabrique
 * est le SEUL point que toutes les familles etalent (`...archiveOps`), et elle
 * recoit deja la collection CONVERTIE, celle qui valide la fiche relue. Une
 * ligne ici, et chaque famille sait se suivre, avec le meme converter que
 * `get` ; une ligne par famille aurait fait 61 copies a garder alignees. Le
 * besoin qui l'a rendue commune : la fiche ouverte d'un ecran declare doit
 * voir arriver ce que le serveur ecrit apres elle (le numero automatique, le
 * calcul refait par le declencheur, `docs/socle/champs-types.md`).
 */
export interface ArchiveOps<T> extends ObserveOps<T> {
  /** Archive le document (`archivedAt` = maintenant). Rend le document relu. */
  archive(id: string): Promise<T>;
  /** Desarchive le document (`archivedAt` = null). Rend le document relu. */
  unarchive(id: string): Promise<T>;
  /** Supprime le document (`deletedAt`, et `archivedAt` s'il etait nul). Rend le document relu. */
  remove(id: string): Promise<T>;
}

export function createArchiveOps<T>(
  db: Firestore,
  col: CollectionReference<T>,
  collectionName: string,
  getActorId: () => string,
  hooks?: ArchiveHooks,
): ArchiveOps<T> {
  /**
   * Le geste commun aux trois verbes : ecrire le patch avec `updatedAt` /
   * `updatedBy` (les Security Rules les EXIGENT a chaque mise a jour), puis
   * RELIRE le document par la ref convertie et le rendre. C'est exactement ce
   * que fait deja `update()` dans chaque repository : le document reel est la
   * source de verite, pas la projection de ce qu'on croit avoir ecrit, et
   * `serverTimestamp()` n'a de valeur qu'apres relecture.
   */
  async function writeThenRead(
    id: string,
    geste: string,
    patch: DocumentData,
    lot?: (batch: WriteBatch) => Promise<void>,
  ): Promise<T> {
    const actor = getActorId();
    const data: DocumentData = {
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    };

    // Ref BRUTE pour l'ecriture partielle (cf. le @param `db` ci-dessus).
    // Avec des ecritures de famille (`hooks`), le patch part dans un LOT avec
    // elles : tout passe, ou rien.
    if (lot === undefined) {
      await updateDoc(doc(db, collectionName, id), data);
    } else {
      const batch = writeBatch(db);
      batch.update(doc(db, collectionName, id), data);
      await lot(batch);
      await batch.commit();
    }

    // Relecture par la ref CONVERTIE : le converter valide le document avec le
    // schema Zod de la famille.
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) {
      throw new Error(
        `${collectionName} ${id} introuvable apres ${geste}. Rechargez la liste : ` +
          'la fiche a disparu entre son ecriture et sa relecture.',
      );
    }
    return snap.data();
  }

  return {
    ...createObserveOps<T>(col),

    async archive(id: string): Promise<T> {
      return writeThenRead(id, 'archivage', { archivedAt: serverTimestamp() });
    },

    async unarchive(id: string): Promise<T> {
      // `null`, JAMAIS `deleteField()` : voir le piege dans l'en-tete du module,
      // et le commentaire de `keepsArchivedAt()` dans firebase/firestore.rules.
      return writeThenRead(id, 'desarchivage', { archivedAt: null });
    },

    async remove(id: string): Promise<T> {
      // Lecture prealable, et sans converter : on ne veut qu'un champ, et un
      // document que son schema Zod refuse (donnee historique, migration en
      // retard) doit rester supprimable. Le prix de cette lecture est justifie
      // dans l'en-tete du module.
      const before = await getDoc(doc(db, collectionName, id));
      if (!before.exists()) {
        // Chemin difficile a atteindre en pratique, garde quand meme : nos rules
        // s'ecrivent `allow read: if isOrgMember(resource.data.orgId)`, et sur un
        // document INEXISTANT `resource` vaut null, donc Firestore repond
        // `permission-denied` avant d'arriver ici (meme constat que
        // `readCursorDoc`, list-query.ts). Sans cette garde, le jour ou une regle
        // change, on ecrirait sur un document absent au lieu de le dire.
        throw new Error(
          `${collectionName} ${id} introuvable : rien a supprimer. ` +
            'La fiche a peut-etre deja ete supprimee ailleurs, rechargez la liste.',
        );
      }

      const archivedAt: unknown = before.get('archivedAt');
      const patch: DocumentData = { deletedAt: serverTimestamp() };
      // Un document deja archive GARDE sa date d'archivage : on ne la remplace
      // pas par l'heure de la suppression (cf. l'en-tete du module).
      if (archivedAt == null) {
        patch['archivedAt'] = serverTimestamp();
      }

      const avantSuppression = hooks?.avantSuppression;
      const avant = before.data();
      return writeThenRead(
        id,
        'suppression',
        patch,
        avantSuppression === undefined ? undefined : (batch) => avantSuppression(id, batch, avant),
      );
    },
  };
}
